/**
 * Banners Tool
 *
 * Tool implementation for Banner Ads tracking
 */

import { FaImage } from "react-icons/fa";
import { BaseTool } from "../BaseTool";
import { bannersApi } from "../../services/toolsApi";

export class BannersTool extends BaseTool {
  constructor() {
    super();

    // Metadata
    this.id = "banners";
    this.name = "Banner Ads";
    this.description = "Banner advertising tracking and analytics";
    this.icon = FaImage;
    this.color = "#8B5CF6";
    this.path = "/dashboard/tools/promoters?tab=banners";
    this.navHidden = true; // reached via the Promoters tool's Banner Ads tab

    // Collections
    this.collections = [
      "banner_tracking",
      "banner_daily_stats",
      "banner_summary",
      "banner_company_summary",
    ];

    // Access control
    this.requiresAdmin = false;

    // Summary configuration
    this.summaryConfig = {
      enabled: true,
      mainStat: "todayViews",
      expectedStatCount: 5, // todayViews, totalViews, todayClicks, totalClicks, ctr
    };

    // Filter configuration
    this.filterConfig = {
      search: {
        enabled: true,
        placeholder: "Search companies...",
        fields: ["companyName", "referralKey"], // Fields to search
      },
      buttonGroups: [], // No button filters for banners
      sortOptions: [
        { value: "views", label: "Most Views" },
        { value: "clicks", label: "Most Clicks" },
        { value: "ctr", label: "Best CTR" },
        { value: "name", label: "Company Name" },
      ],
      defaultSort: "views",
    };
  }

  /**
   * Get summary data for dashboard widget
   */
  async getSummaryData() {
    try {
      const response = await bannersApi.getAllStats();

      if (!response) {
        throw new Error("Invalid response from bannersApi");
      }

      return {
        success: true,
        data: {
          todayViews: response.totalImageViewsToday || 0,
          totalViews: response.totalImageViews || 0,
          todayClicks: response.totalClicksToday || 0,
          totalClicks: response.totalClicks || 0,
          ctr: response.overallClickThroughRate || "0%",
        },
        lastUpdated: new Date(),
      };
    } catch (error) {
      console.error("Error fetching banner summary:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Get detailed stats for the tool page
   * This method is specific to BannersTool and not part of BaseTool
   */
  async getDetailedStats() {
    try {
      const response = await bannersApi.getAllStats();
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Custom backup preparation
   * Could exclude test banners or inactive campaigns
   */
  async prepareBackup(collections, format) {
    // For CSV format, might want to flatten the banner data structure
    if (format === "csv") {
      // Future: Add custom CSV formatting logic
    }

    return collections;
  }
}

export default BannersTool;
